import clsx from "clsx";
import { AlertTriangle, Info, Lightbulb } from "lucide-react";
import type { FeatureFlag } from "@/config/featureFlags";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Section {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ElementType;
  adminOnly?: boolean;
  /** When set, this section is hidden unless the corresponding feature flag is enabled. */
  featureFlag?: FeatureFlag;
  content: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Helper components
// ---------------------------------------------------------------------------

/** Standard content card – now with an optional accent left-border */
export function SectionCard({
  title,
  accent,
  children,
}: Readonly<{
  title: string;
  accent?: "orange" | "purple" | "green" | "blue" | "amber";
  children: React.ReactNode;
}>) {
  const borderColor: Record<string, string> = {
    orange: "border-l-brand-primary",
    purple: "border-l-brand-secondary",
    green: "border-l-green-500",
    blue: "border-l-blue-500",
    amber: "border-l-amber-500",
  };
  return (
    <div
      className={clsx(
        "bg-surface-primary border border-border-primary rounded-xl p-6",
        accent && `border-l-4 ${borderColor[accent]}`,
      )}
    >
      <h3 className="text-lg font-semibold text-content-primary mb-3">{title}</h3>
      <div className="text-content-secondary text-sm leading-relaxed space-y-2">{children}</div>
    </div>
  );
}

/** Colored callout box — great for tips, notes, and heads-ups */
export function Callout({
  variant = "tip",
  title,
  children,
}: Readonly<{
  variant?: "tip" | "info" | "warning";
  title?: string;
  children: React.ReactNode;
}>) {
  const styles = {
    tip: {
      bg: "bg-green-500/5",
      border: "border-green-500/30",
      icon: <Lightbulb className="w-4 h-4 text-green-400 shrink-0 mt-0.5" />,
      label: "Pro tip",
      text: "text-green-300",
    },
    info: {
      bg: "bg-blue-500/5",
      border: "border-blue-500/30",
      icon: <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />,
      label: "Good to know",
      text: "text-blue-300",
    },
    warning: {
      bg: "bg-amber-500/5",
      border: "border-amber-500/30",
      icon: <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5" />,
      label: "Heads up",
      text: "text-amber-300",
    },
  };
  const s = styles[variant];
  return (
    <div className={clsx("rounded-lg border-l-4 p-4 flex gap-3", s.bg, s.border)}>
      {s.icon}
      <div className="text-sm leading-relaxed space-y-1">
        <p className={clsx("font-semibold", s.text)}>{title ?? s.label}</p>
        <div className="text-content-secondary">{children}</div>
      </div>
    </div>
  );
}

/** Inline keyboard shortcut badge */
export function Kbd({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <kbd className="inline-block px-1.5 py-0.5 rounded-sm bg-surface-tertiary border border-border-hover text-[11px] font-mono text-content-secondary leading-none align-middle">
      {children}
    </kbd>
  );
}

/** Numbered step card for walkthroughs */
export function StepCard({
  step,
  title,
  children,
}: Readonly<{
  step: number;
  title: string;
  children: React.ReactNode;
}>) {
  return (
    <div className="flex gap-4 items-start">
      <div className="w-9 h-9 rounded-full bg-brand-gradient-br flex items-center justify-center text-white font-bold text-sm shrink-0">
        {step}
      </div>
      <div className="flex-1 pt-1">
        <p className="text-content-primary font-semibold mb-1">{title}</p>
        <p className="text-content-secondary text-sm leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

/** Mini feature highlight grid — breaks the wall-of-bullets monotony */
export function FeatureGrid({ children }: Readonly<{ children: React.ReactNode }>) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

export function FeatureGridItem({
  title,
  children,
}: Readonly<{
  title: string;
  children: React.ReactNode;
}>) {
  return (
    <div className="bg-surface-secondary/60 border border-border-secondary/50 rounded-lg p-4">
      <p className="text-content-primary font-medium text-sm mb-1">{title}</p>
      <p className="text-content-tertiary text-xs leading-relaxed">{children}</p>
    </div>
  );
}
